import { CornerDownRight, Star } from 'lucide-react';
import type { GbpReviewView } from '@pageboss/shared';
import { ReviewReplyBox } from './review-reply-box';
import { cn } from '@/lib/utils';

interface GbpReviewsListProps {
  reviews: GbpReviewView[];
  siteId: string;
  canReply: boolean;
  onChanged: (announcement?: string) => void;
}

export function GbpReviewsList({ reviews, siteId, canReply, onChanged }: GbpReviewsListProps) {
  if (reviews.length === 0) {
    return <p className="text-sm text-text-tertiary">No reviews yet.</p>;
  }
  return (
    <ul className="flex flex-col divide-y divide-border-subtle">
      {reviews.map((r) => (
        <li key={r.id} className="py-4 first:pt-0 last:pb-0">
          <ReviewRow review={r} siteId={siteId} canReply={canReply} onChanged={onChanged} />
        </li>
      ))}
    </ul>
  );
}

function reviewerDisplayName(review: GbpReviewView): string {
  return review.reviewerName ?? 'A Google user';
}

function ReviewRow({
  review,
  siteId,
  canReply,
  onChanged,
}: {
  review: GbpReviewView;
  siteId: string;
  canReply: boolean;
  onChanged: (announcement?: string) => void;
}) {
  const name = reviewerDisplayName(review);
  return (
    <article className="flex gap-3">
      <Avatar name={name} />
      <div className="flex-1 min-w-0">
        <header className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <span className="text-sm font-medium text-foreground">{name}</span>
            <span className="ml-2 text-xs text-text-tertiary">{relativeDate(review.postedAt)}</span>
          </div>
          <Stars rating={review.rating} />
        </header>
        {review.text ? (
          <p className="mt-1.5 text-sm leading-relaxed text-text-secondary">{review.text}</p>
        ) : null}

        {review.ownerReply ? (
          <div className="mt-3 rounded-md bg-brand-50 px-3 py-2">
            <div className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700">
              <CornerDownRight className="h-3 w-3" aria-hidden />
              You replied
              {review.ownerReply.repliedAt ? ` · ${relativeDate(review.ownerReply.repliedAt)}` : ''}
            </div>
            <p className="mt-1 text-sm leading-relaxed text-text-secondary">
              {review.ownerReply.text}
            </p>
          </div>
        ) : (
          <ReviewReplyBox
            siteId={siteId}
            reviewId={review.id}
            initialDraft={review.replyDraft ?? null}
            canReply={canReply}
            onChanged={onChanged}
          />
        )}
      </div>
    </article>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <div className="inline-flex items-center gap-0.5 shrink-0" aria-label={`${rating} stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={cn(
            'h-3.5 w-3.5',
            n <= rating ? 'fill-amber-400 text-amber-400' : 'text-border',
          )}
          aria-hidden
        />
      ))}
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  const initial = (name.trim().charAt(0) || '?').toUpperCase();
  return (
    <div
      className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full bg-surface-sunken text-xs font-semibold text-text-secondary"
      aria-hidden
    >
      {initial}
    </div>
  );
}

function relativeDate(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffDays = Math.round((now - then) / (1000 * 60 * 60 * 24));
  if (diffDays < 1) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.round(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.round(diffDays / 30)}mo ago`;
  return `${Math.round(diffDays / 365)}y ago`;
}
